<template>
  <article class="prc" :class="`prc--${state}`">
    <!-- Figma Content column -->
    <div class="prc__content">
      <nav class="prc__breadcrumbs" aria-label="Breadcrumb">
        <button
          type="button"
          class="prc__crumb prc__crumb--muted"
          @click="$emit('navigate-list')"
        >
          {{ listBreadcrumb }}
        </button>
        <span class="prc__crumb-sep" aria-hidden="true">
          <img :src="chevronIcon" alt="" width="16" height="16" />
        </span>
        <span class="prc__crumb prc__crumb--current">{{ detailBreadcrumb }}</span>
      </nav>

      <div class="prc__headline">
        <div class="prc__info">
          <h1 class="prc__title">{{ name }}</h1>

          <div v-if="effectiveLine" class="prc__effective">
            <span v-if="effectiveLine" class="prc__effective-text">{{
              effectiveLine
            }}</span>
          </div>

          <div class="prc__details">
            <div class="prc__badges-meta">
              <span
                v-if="badge"
                class="prc__badge"
                :class="`prc__badge--${BADGE_TONE_CLASS[badge.tone]}`"
              >
                {{ badge.label }}
              </span>
              <span v-if="tag" class="prc__badge prc__badge--gray">{{ tag.label }}</span>
              <span
                v-if="draftPendingLabel"
                class="prc__badge prc__badge--draft-pending"
              >
                {{ draftPendingLabel }}
              </span>
              <div class="prc__meta">
                <img
                  v-if="flagSrc"
                  class="prc__flag"
                  :src="flagSrc"
                  alt=""
                  width="12"
                  height="9"
                />
                <span v-if="location" class="prc__meta-item">{{ location }}</span>
                <template v-for="(item, index) in metaTrail">
                  <img
                    :key="`sep-${index}`"
                    class="prc__meta-sep"
                    :src="metaSepIcon"
                    alt=""
                    width="14"
                    height="20"
                    aria-hidden="true"
                  />
                  <span :key="`item-${index}`" class="prc__meta-item">{{
                    item
                  }}</span>
                </template>
              </div>
            </div>

            <div v-if="readiness.length" class="prc__readiness">
              <p class="prc__readiness-labels">
                <span class="prc__readiness-prefix"
                  >Pay Range vs Readiness @
                </span>
                <template v-for="(point, index) in readiness">
                  <span
                    :key="`lbl-${index}`"
                    :class="`prc__tone--${point.tone || 'neutral'}`"
                    >{{ point.label }}</span
                  ><span
                    v-if="index < readiness.length - 1"
                    :key="`ld-${index}`"
                    class="prc__readiness-dash"
                    > - </span
                  >
                </template>
              </p>
              <div class="prc__readiness-values">
                <template v-for="(point, index) in readiness">
                  <span
                    :key="`val-${index}`"
                    class="prc__readiness-value"
                    :class="`prc__tone--${point.tone || 'neutral'}`"
                    >{{ point.value }}</span
                  >
                  <span
                    v-if="index < readiness.length - 1"
                    :key="`vd-${index}`"
                    class="prc__readiness-value-dash"
                    >-</span
                  >
                </template>
              </div>
            </div>
          </div>
        </div>

        <div class="prc__actions">
          <button
            v-for="action in actions"
            :key="action.key"
            type="button"
            class="prc__btn"
            :class="`prc__btn--${action.variant}`"
            @click="$emit(action.emit)"
          >
            {{ action.label }}
          </button>
          <DropDownMenu
            v-if="showMenu"
            :dropdownItems="menuItems"
            :is-open="false"
            :width="160"
            tipSide="right"
            :translate-y-offset="8"
            :translate-x-offset="0"
            @onClick="onMenuAction"
          >
            <template #activator>
              <button
                type="button"
                class="prc__menu"
                aria-label="More actions"
              >
                <img
                  class="prc__menu-icon"
                  :src="dotsIcon"
                  alt=""
                  width="4"
                  height="18"
                />
              </button>
            </template>
          </DropDownMenu>
        </div>
      </div>

      <!-- Figma embeds PayRangeTimeline inside the card in every state that has versions -->
      <PayRangeVersionTimeline
        v-if="timelineVisible"
        class="prc__timeline"
        :versions="versions"
        :today="today"
        :handoff-date="handoffDate"
        :handoff-label="handoffLabel"
        :currency-symbol="currencySymbol"
        :unit-suffix="unitSuffix"
        :selected-version="selectedVersion"
        @select="onSelectVersion"
      />

      <!-- ER-11221 / ER-11243: version history list, inline below the timeline (Figma 3439:6941) -->
      <PayRangeVersionHistoryList
        v-if="versionHistoryVisible"
        class="prc__version-list"
        :versions="historyRows"
        :today="today"
        :unit-suffix="unitSuffix"
        :selected-version="selectedVersion"
        @select="onSelectVersion"
        @correct="$emit('correct', $event)"
        @void="$emit('void', $event)"
        @discard-draft="$emit('discard-draft')"
        @publish-draft="$emit('publish-draft')"
        @edit-draft="$emit('edit-draft')"
      />
    </div>
  </article>
</template>

<script setup>
import { computed, ref } from "vue";
import PayRangeVersionTimeline from "@components/PayRangeVersionTimeline/PayRangeVersionTimeline.vue";
import PayRangeVersionHistoryList from "@components/PayRangeVersionHistoryList/PayRangeVersionHistoryList.vue";
import DropDownMenu from "@components/DropDownMenu/DropDownMenu.vue";
import { archivedTag as toArchivedTag, rangeStatusBadge } from "@components/FairPay/payRangeBadges";
import chevronIcon from "./icon-chevron-right.svg";
import dotsIcon from "./icon-dots-vertical.svg";
import metaSepIcon from "./meta-sep.svg";

const PUBLISHED_MENU_ITEMS = [
  {
    name: "Retire",
    value: "retire",
    icon: "icons/dropdown/activate.svg",
  },
];

const props = defineProps({
  /** `{ label, tone }` from rangeStatusBadge(); derived from `state` when omitted. */
  statusBadge: { type: Object, default: null },
  /** `{ label: "Archived" }` from archivedTag(); shown after the status badge. */
  archivedTag: { type: Object, default: null },
  state: {
    type: String,
    default: "current",
    validator: (v) =>
      [
        "first-draft",
        "current",
        "published-multi",
        "published-multi-no-draft",
        "retired",
        "archived",
      ].includes(v),
  },
  name: { type: String, required: true },
  listBreadcrumb: { type: String, default: "Pay Ranges" },
  detailBreadcrumb: { type: String, default: "Pay Range" },
  effectiveLine: { type: String, default: "" },
  draftPendingLabel: { type: String, default: "" },
  flag: { type: String, default: "" },
  location: { type: String, default: "" },
  workMode: { type: String, default: "" },
  payUnit: { type: String, default: "" },
  code: { type: String, default: "" },
  readiness: { type: Array, default: () => [] },
  versions: { type: Array, default: () => [] },
  today: { type: [String, Date], default: () => new Date() },
  handoffDate: { type: [String, Date], default: null },
  /** Marker text above draft start — Figma PayRangeCard uses "Effective Date". */
  handoffLabel: { type: String, default: "Effective Date" },
  /** Timeline bar range prefix: "$" or currency code (e.g. "RON "). */
  currencySymbol: { type: String, default: "$" },
  unitSuffix: { type: String, default: "USD/year" },
  showTimeline: { type: Boolean, default: undefined },
  /**
   * Version-history rows (ER-11243) in PayRangeVersionHistoryList shape:
   * { key, versionNumber, versionLabel, status, startDate, endDate,
   *   minAmount, maxAmount, usageCount }. Falls back to `versions` when empty.
   */
  versionHistory: { type: Array, default: () => [] },
});

const ACTION_CONFIG = {
  "first-draft": [
    {
      key: "discard",
      label: "Discard Draft",
      variant: "tertiary-error",
      emit: "discard-draft",
    },
    { key: "edit", label: "Edit", variant: "secondary", emit: "edit" },
    { key: "publish", label: "Publish", variant: "primary", emit: "publish" },
  ],
  current: [
    {
      key: "new-version",
      label: "New Version",
      variant: "primary",
      emit: "new-version",
    },
  ],
  "published-multi": [
    {
      key: "edit-draft",
      label: "Edit Draft",
      variant: "secondary",
      emit: "edit-draft",
    },
    {
      key: "publish-draft",
      label: "Publish Draft",
      variant: "primary",
      emit: "publish-draft",
    },
  ],
  "published-multi-no-draft": [
    {
      key: "new-version",
      label: "New Version",
      variant: "primary",
      emit: "new-version",
    },
  ],
  retired: [
    {
      key: "archive",
      label: "Archive",
      variant: "secondary-error",
      emit: "archive",
    },
    {
      key: "reactivate",
      label: "Reactivate",
      variant: "primary",
      emit: "reactivate",
    },
  ],
  archived: [
    {
      key: "unarchive",
      label: "Unarchive",
      variant: "primary",
      emit: "unarchive",
    },
  ],
};

const STATE_RANGE_STATUS = {
  "first-draft": "DRAFT",
  current: "IN_FORCE",
  "published-multi": "IN_FORCE",
  "published-multi-no-draft": "IN_FORCE",
  retired: "RETIRED",
  archived: "RETIRED",
};

const BADGE_TONE_CLASS = { draft: "gray", "in-force": "success", terminated: "gray" };

const emit = defineEmits([
  "navigate-list",
  "discard-draft",
  "edit",
  "publish",
  "new-version",
  "edit-draft",
  "publish-draft",
  "archive",
  "reactivate",
  "unarchive",
  "retire",
  "more",
  "correct",
  "void",
  "open-version",
]);

const actions = computed(() => ACTION_CONFIG[props.state] || []);

/** ⋯ menu: Retire for published states (Archive/Reactivate are primary on Retired). */
const showMenu = computed(() =>
  ["current", "published-multi", "published-multi-no-draft"].includes(props.state)
);

const menuItems = computed(() => (showMenu.value ? PUBLISHED_MENU_ITEMS : []));

function onMenuAction(item) {
  const value = item?.value;
  if (value === "retire") {
    emit("retire");
    return;
  }
  if (value) emit(value);
  emit("more", item);
}

const badge = computed(
  () => props.statusBadge || rangeStatusBadge(STATE_RANGE_STATUS[props.state])
);

const tag = computed(() => props.archivedTag || (props.state === "archived" ? toArchivedTag("ARCHIVED") : null));

/** Timeline shows in every state whenever there are versions to plot; `showTimeline` overrides. */
const timelineVisible = computed(() => {
  if (props.showTimeline !== undefined) return Boolean(props.showTimeline);
  return (props.versions || []).length > 0;
});

/** Accept both the numeric `version` and legacy `"v1"` string forms. */
function toVersionNumber(value) {
  const str = String(value ?? "");
  const parsed = Number(str.replace(/^v/i, ""));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : value;
}

const selectedVersion = ref(null);

function onSelectVersion(version, payload) {
  const next = toVersionNumber(version);
  const n = typeof next === "number" ? next : Number(next);
  if (!Number.isFinite(n) || n <= 0) return;
  selectedVersion.value = n;
  const row =
    payload ||
    historyRows.value.find((r) => Number(r.versionNumber) === n && r.status !== "voided");
  if (row) emit("open-version", row);
}

/** Timeline bar states → history row statuses (Figma badges). */
const HISTORY_STATUS = {
  past: "passed",
  passed: "passed",
  current: "current",
  draft: "draft",
  voided: "voided",
  future: "future",
};

/**
 * Version-history rows for the inline list (ER-11243). Consumers that own the
 * per-version payload (usage counts included) pass `versionHistory`; otherwise
 * the rows are derived from the timeline `versions` so the card stays usable
 * standalone — usage counts are unknown there, so they render as "Not used".
 */
const historyRows = computed(() => {
  if ((props.versionHistory || []).length) return props.versionHistory;

  return (props.versions || []).map((v, index) => {
    const versionNumber = toVersionNumber(v.version) || index + 1;
    const state = String(v.state ?? v.status ?? "").toLowerCase();
    return {
      key: `${versionNumber}-${v.effectiveFrom ?? v.startDate ?? "open"}`,
      versionNumber,
      versionLabel: `Version ${versionNumber}`,
      status: HISTORY_STATUS[state] || "passed",
      startDate: v.effectiveFrom ?? v.startDate ?? null,
      endDate: v.effectiveTo ?? v.endDate ?? null,
      minAmount: v.bottom ?? v.minAmount ?? null,
      maxAmount: v.top ?? v.maxAmount ?? null,
      usageCount: 0,
    };
  });
});

/** Figma embeds the list under the timeline for every state that has rows. */
const versionHistoryVisible = computed(() => historyRows.value.length > 0);

const metaTrail = computed(() =>
  [props.workMode, props.payUnit, props.code].filter(Boolean)
);

const flagSrc = computed(() => {
  if (!props.flag) return "";
  if (/^https?:\/\//i.test(props.flag) || props.flag.includes("/")) {
    return props.flag;
  }
  return `https://flagcdn.com/${props.flag.toLowerCase()}.svg`;
});
</script>

<style scoped>
/* Tokens from Figma PayRangeCard node 1969:6733 */
.prc {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  padding: 20px;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  background: #f9fafb;
  box-shadow:
    0 1px 3px rgba(16, 24, 40, 0.1),
    0 1px 2px rgba(16, 24, 40, 0.06);
  overflow: hidden;
  box-sizing: border-box;
}

.prc--current,
.prc--published-multi,
.prc--published-multi-no-draft {
  background: #ffffff;
}

.prc--archived {
  background: #fef3f2;
}

.prc__content {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  min-width: 0;
}

.prc__breadcrumbs {
  display: flex;
  align-items: center;
  gap: 8px;
}

.prc__crumb {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  border: none;
  border-radius: 6px;
  font-family: Inter, sans-serif;
  font-size: 14px;
  line-height: 20px;
  white-space: nowrap;
}

.prc__crumb--muted {
  background: transparent;
  color: #475467;
  font-weight: 500;
  cursor: pointer;
}

.prc__crumb--muted:hover {
  background: #f9fafb;
}

.prc__crumb--current {
  background: #f9f5ff;
  color: #6941c6;
  font-weight: 600;
}

.prc__crumb-sep {
  display: inline-flex;
  width: 16px;
  height: 16px;
  overflow: hidden;
  flex-shrink: 0;
}

.prc__crumb-sep img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.prc__headline {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
}

.prc__info {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
  justify-content: center;
}

.prc__title {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 30px;
  font-weight: 600;
  line-height: 38px;
  color: #101828;
}

.prc__effective {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  font-family: Inter, sans-serif;
  font-size: 12px;
  line-height: normal;
}

.prc__effective-text {
  color: #667085;
  font-weight: 500;
}

.prc__details {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.prc__badges-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}

.prc__badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 9999px;
  border: 1px solid transparent;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  white-space: nowrap;
}

.prc__badge--gray {
  background: #f9fafb;
  border-color: #eaecf0;
  color: #344054;
}

.prc__badge--success {
  background: #ecfdf3;
  border-color: #abefc6;
  color: #067647;
}

.prc__badge--error {
  background: #fef3f2;
  border-color: #fecdca;
  color: #b42318;
}

.prc__badge--draft-pending {
  background: var(--component-colors-utility-fuchsia-utility-fuchsia-50, #fdf4ff);
  border: 1px dashed var(--component-colors-utility-fuchsia-utility-fuchsia-500, #d444f1);
  color: var(--colors-text-text-primary-900, #101828);
  line-height: normal;
}

.prc__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0;
  min-width: 0;
}

.prc__flag {
  width: 12px;
  height: 9px;
  margin-right: 3px;
  object-fit: cover;
  border-radius: 1px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-sizing: border-box;
  flex-shrink: 0;
}

.prc__meta-item {
  font-family: Inter, sans-serif;
  font-size: 10px;
  font-weight: 500;
  line-height: 20px;
  color: #101828;
  white-space: nowrap;
}

.prc__meta-sep {
  display: block;
  width: 14px;
  height: 20px;
  flex-shrink: 0;
}

.prc__readiness-labels {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  white-space: nowrap;
}

.prc__readiness-prefix,
.prc__readiness-dash {
  color: #667085;
}

.prc__readiness-values {
  display: flex;
  align-items: center;
  gap: 5px;
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  white-space: nowrap;
}

.prc__readiness-value-dash {
  color: #344054;
}

.prc__tone--neutral {
  color: #344054;
}

.prc__tone--danger {
  color: #ff0303;
}

.prc__tone--success {
  color: #17b26a;
}

.prc__tone--info {
  color: #00a3ff;
}

.prc__actions {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 10px;
}

.prc__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 14px;
  border-radius: 8px;
  border: 1px solid transparent;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  white-space: nowrap;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  box-sizing: border-box;
}

.prc__btn--primary {
  background: #7f56d9;
  border-color: #7f56d9;
  color: #fff;
}

.prc__btn--primary:hover {
  background: #6941c6;
  border-color: #6941c6;
}

.prc__btn--secondary {
  background: #fff;
  border-color: #d0d5dd;
  color: #344054;
}

.prc__btn--secondary:hover {
  background: #f9fafb;
}

.prc__btn--secondary-error {
  background: #fff;
  border-color: #fda29b;
  color: #b42318;
}

.prc__btn--secondary-error:hover {
  background: #fef3f2;
}

.prc__btn--tertiary-error {
  padding: 8px 12px;
  background: transparent;
  border-color: transparent;
  box-shadow: none;
  color: #b42318;
}

.prc__btn--tertiary-error:hover {
  background: #fef3f2;
}

.prc__menu {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  overflow: hidden;
  flex-shrink: 0;
}

.prc__menu-icon {
  display: block;
  width: 4px;
  height: 18px;
  object-fit: contain;
}

/* Embedded Figma PayRangeTimeline — full width of card */
.prc__timeline {
  width: 100% !important;
  min-width: 0 !important;
  max-width: 100%;
  flex-shrink: 0;
}

/* Inline version-history list (ER-11243) — full width of card */
.prc__version-list {
  width: 100%;
  min-width: 0;
  flex-shrink: 0;
}

@media (max-width: 900px) {
  .prc__headline {
    flex-direction: column;
  }

  .prc__actions {
    flex-wrap: wrap;
    width: 100%;
  }

  .prc__readiness-labels,
  .prc__readiness-values {
    white-space: normal;
  }
}
</style>
